import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import type { SettingsState } from './settingsTypes';

export class SettingsService {
  private static instance: SettingsService;

  private constructor() {}

  public static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      SettingsService.instance = new SettingsService();
    }
    return SettingsService.instance;
  }

  public async getSettings(): Promise<SettingsState> {
    const settingsCollection = collection(db, 'metadata');
    // get all docs in the collection
    const settingsDocs = await getDocs(settingsCollection);
    const settingsMap = new Map(
      settingsDocs.docs.map((doc) => {
        const data = doc.data();
        return [doc.id, data];
      })
    );
    const settings: SettingsState = {
      paywallShowPerWeekMode: settingsMap.get('app_settings')?.paywall_show_per_week_mode || false,
      onboardingShowAskForReview: settingsMap.get('app_settings')?.onboarding_show_ask_for_review ?? true,
      communityFeaturedQuestion: settingsMap.get('community_featured_question')?.questionId || '',
      communityFeaturedSuccessStory: settingsMap.get('community_featured_success_story')?.successStoryId || '',
      journeyDailyMaxBrainPoints: settingsMap.get('app_settings')?.journey_daily_max_brain_points ?? 25,
      journeyInitialEnergy: settingsMap.get('app_settings')?.journey_initial_energy ?? 15,
      // Defaults match the constants in obsessless-api/src/common/constants.ts.
      sttSessionsPerWeek: settingsMap.get('app_settings')?.stt_sessions_per_week ?? 500,
      voiceConversationsPerWeek: settingsMap.get('app_settings')?.voice_conversations_per_week ?? 20,
      voiceMinutesPerWeek: settingsMap.get('app_settings')?.voice_minutes_per_week ?? 120,
      maxVoiceCallMinutes: settingsMap.get('app_settings')?.max_voice_call_minutes ?? 60,
    };

    return settings;
  }

  public async updatePaywallShowPerWeekMode(paywallShowPerWeekMode: boolean): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { paywall_show_per_week_mode: paywallShowPerWeekMode });
  }

  public async updateOnboardingShowAskForReview(onboardingShowAskForReview: boolean): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { onboarding_show_ask_for_review: onboardingShowAskForReview });
  }

  public async updateCommunityFeaturedQuestion(communityFeaturedQuestion: string): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'community_featured_question'), { questionId: communityFeaturedQuestion });
  }

  public async updateCommunityFeaturedSuccessStory(communityFeaturedSuccessStory: string): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'community_featured_success_story'), { successStoryId: communityFeaturedSuccessStory });
  }

  public async updateJourneyDailyMaxBrainPoints(journeyDailyMaxBrainPoints: number): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { journey_daily_max_brain_points: journeyDailyMaxBrainPoints });
  }

  public async updateJourneyInitialEnergy(journeyInitialEnergy: number): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { journey_initial_energy: journeyInitialEnergy });
  }

  public async updateSttSessionsPerWeek(sttSessionsPerWeek: number): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { stt_sessions_per_week: sttSessionsPerWeek });
  }

  public async updateVoiceConversationsPerWeek(voiceConversationsPerWeek: number): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { voice_conversations_per_week: voiceConversationsPerWeek });
  }

  public async updateVoiceMinutesPerWeek(voiceMinutesPerWeek: number): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { voice_minutes_per_week: voiceMinutesPerWeek });
  }

  public async updateMaxVoiceCallMinutes(maxVoiceCallMinutes: number): Promise<void> {
    const settingsCollection = collection(db, 'metadata');
    await updateDoc(doc(settingsCollection, 'app_settings'), { max_voice_call_minutes: maxVoiceCallMinutes });
  }
}

export const settingsService = SettingsService.getInstance();
